import { useState } from "react";
import { Button, Tooltip } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import GenericFormPopup from "../../Fleet/GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import {
  orientationExpenseFieldConfiguration,
  orientationExpenseInitialValues,
} from "../../../formConfigs/Recruiting/orientationExpenseFieldConfig";

interface OrientationExpensePopupProps {
  orientationId?: string;
  orientationDriverFirstName?: string | null;
  orientationDriverLastName?: string | null;
}

const OrientationExpensePopup = ({
  orientationId,
  orientationDriverFirstName,
  orientationDriverLastName,
}: OrientationExpensePopupProps) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const initialValues = {
    ...orientationExpenseInitialValues,
    driverFirstName: orientationDriverFirstName ?? "",
    driverLastName: orientationDriverLastName ?? "",
  };

  const expenseMutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(
        `${apiVariable}/recruiting/orientations/${orientationId}/expense`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      ),
    onSuccess: () => {
      setStatus("success");
      setOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["orientationExpenses", orientationId],
      });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to submit expense");
    },
  });

  const submitHandler = (data: any) => {
    if (!orientationId) return;
    expenseMutation.mutate({
      ...data,
      orientationId,
    });
  };

  const disabled = !orientationId;

  return (
    <>
      <Tooltip
        title={
          disabled
            ? "Select an orientation to add an expense"
            : "Create expense"
        }
      >
        <span>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpen(true)}
            disabled={disabled}
          >
            Create Expense
          </Button>
        </span>
      </Tooltip>
      <GenericFormPopup
        formFields={orientationExpenseFieldConfiguration}
        initialValues={initialValues}
        title="Orientation Expense"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        loading={expenseMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Expense created successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default OrientationExpensePopup;
